import { FC, HTMLAttributes } from "react"

import clsx from "clsx"

import useClusterSWR from "@/hooks/useClusterSWR"

import { Image } from "@/components/Image"

import { UserProfile } from "@/API"

/**
 * Avatar component for displaying a user's profile image or initials.
 * Accepts a user object, optional size, and additional div attributes.
 *
 * @param {Object} props - The props for the Avatar component.
 * @param {User} props.user - The user object containing profile info.
 * @param {string} [props.size] - The size of the avatar in pixels (as a string).
 * @returns {JSX.Element|null} The rendered avatar or null if no user.
 */
export const Avatar: FC<{ user: Partial<UserProfile>; size?: `${number}` } & HTMLAttributes<HTMLDivElement>> = ({
  user,
  size = "32",
  className,
  style,
  children,
  ...props
}) => {
  const sizeI = parseInt(size, 10)
  const { name, image } = user || {}
  const { data: pictureData } = useClusterSWR<string>(
    image && !image.startsWith("http") && !image.startsWith("blob:") ? `assets/get_presigned_path/${image}` : null,
    // Refresh once per hour
    {
      revalidateOnFocus: false,
      refreshInterval: 60 * 60 * 1000,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
    "POST"
  )
  const picture = image?.startsWith("http") || image?.startsWith("blob:") ? image : pictureData

  return (
    <div
      className={clsx(
        className,
        "dark:bg-charcoal bg-bonjour relative flex shrink-0 items-center justify-center rounded-full text-center font-normal"
      )}
      style={{
        fontSize: `${sizeI / 2 / 16}rem`,
        width: sizeI,
        height: sizeI,
        ...style,
      }}
      {...props}
    >
      {!picture && <span aria-hidden="true">{(name || "").charAt(0).toUpperCase()}</span>}
      {picture && (
        <Image
          src={picture}
          alt={name || ""}
          className="rounded-full object-cover"
          style={{ width: sizeI, height: sizeI }}
        />
      )}
      <div className="absolute inset-0 rounded-full border border-black opacity-10" />
      {children}
    </div>
  )
}

export default Avatar
